import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import {
	DashboardVisibility,
	ReportDashboard,
	StoredDashboard,
	StoredDashboardsResponse,
} from "../models/ReportBuilder";
import { apiStore } from "../stores/api.store";

export const DASHBOARDS_KEY = ["report-dashboards"];

/**
 * The user's dashboards as stored on the server: their own in full, plus the
 * ones colleagues share with the organisation as summaries (opened one at a
 * time with `useSharedDashboard`).
 */
export function useStoredDashboards(enabled: boolean) {
	return useQuery<StoredDashboardsResponse, ApiErrorResponse>(
		DASHBOARDS_KEY,
		() =>
			apiStore
				.get<StoredDashboardsResponse>({ path: "/ai/report/dashboards", isAuthRequired: true })
				.then((d) => d.data),
		{ enabled, retry: false, staleTime: 60 * 1000 }
	);
}

/** One dashboard in full: the caller's own, or one shared with the organisation. */
export function useSharedDashboard(dashboardId: string | undefined) {
	return useQuery<StoredDashboard, ApiErrorResponse>(
		[...DASHBOARDS_KEY, dashboardId],
		() =>
			apiStore
				.get<StoredDashboard>({
					path: `/ai/report/dashboards/${dashboardId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled: !!dashboardId, retry: false }
	);
}

/** Creates or replaces one of the user's own dashboards. */
export function useSaveDashboard() {
	const queryClient = useQueryClient();
	return useMutation<
		StoredDashboard,
		ApiErrorResponse,
		{ dashboard: ReportDashboard; visibility: DashboardVisibility }
	>(
		({ dashboard, visibility }) =>
			apiStore
				.put<StoredDashboard>({
					path: `/ai/report/dashboards/${dashboard.id}`,
					// `sharedWith` rides alongside the document rather than inside it:
					// the backend keeps it in its own field to decide access.
					body: { dashboard, visibility, sharedWith: dashboard.sharedWith },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ onSuccess: () => queryClient.invalidateQueries(DASHBOARDS_KEY) }
	);
}

export function useDeleteDashboard() {
	const queryClient = useQueryClient();
	return useMutation<unknown, ApiErrorResponse, { dashboardId: string }>(
		({ dashboardId }) =>
			apiStore.delete({ path: `/ai/report/dashboards/${dashboardId}`, isAuthRequired: true }),
		{ onSuccess: () => queryClient.invalidateQueries(DASHBOARDS_KEY) }
	);
}
